<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSubmitting = ref(false);
const hasLoaded = ref(false);
const blockedUsers = ref([]);
const searchQuery = ref('');
const waIdInput = ref('');
const updatingWaId = ref('');

const filteredBlockedUsers = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return blockedUsers.value;

  return blockedUsers.value.filter(user =>
    [user.wa_id, user.user, user.user_id, user.input, user.phone_number]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});
const sanitizedWaId = computed(() => waIdInput.value.replace(/[^\d]/g, ''));
const hasWaIdFormatError = computed(
  () => !!waIdInput.value.trim() && sanitizedWaId.value.length < 8
);

const loadBlockedUsers = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppBlockedUsers(props.inbox.id);
    blockedUsers.value = response.data?.payload || [];
    hasLoaded.value = true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const blockUser = async () => {
  const waId = sanitizedWaId.value.trim();
  if (!waId) return;
  if (hasWaIdFormatError.value) {
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_WA_ID_ERROR')
    );
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await InboxesAPI.blockWhatsAppUser(props.inbox.id, waId);
    blockedUsers.value = response.data?.payload || blockedUsers.value;
    waIdInput.value = '';
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_BLOCK_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_BLOCK_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const unblockUser = async waId => {
  updatingWaId.value = waId;
  try {
    const response = await InboxesAPI.unblockWhatsAppUser(props.inbox.id, waId);
    blockedUsers.value = response.data?.payload || [];
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_UNBLOCK_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_UNBLOCK_ERROR')
    );
  } finally {
    updatingWaId.value = '';
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) {
      loadBlockedUsers();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_REFRESH')"
        :is-loading="isLoading"
        @click="loadBlockedUsers"
      />
    </div>

    <div class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4 lg:grid-cols-[minmax(0,2fr)_auto]">
      <woot-input
        v-model="waIdInput"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_WA_ID')"
        :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_WA_ID_PLACEHOLDER')"
      />
      <p class="-mt-2 mb-0 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_WA_ID_HINT') }}
      </p>
      <p
        v-if="hasWaIdFormatError"
        class="-mt-2 mb-0 text-xs text-n-ruby-11"
      >
        {{
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_WA_ID_ERROR')
        }}
      </p>
      <div class="flex items-end">
        <Button
          type="button"
          color="ruby"
          icon="i-ph-prohibit-inset"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_BLOCK')"
          :disabled="!sanitizedWaId.trim() || hasWaIdFormatError"
          :is-loading="isSubmitting"
          @click="blockUser"
        />
      </div>
    </div>

    <woot-input
      v-model="searchQuery"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_SEARCH')"
      :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_SEARCH_PLACEHOLDER')"
    />

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_LOADING') }}
    </div>

    <div
      v-else-if="!blockedUsers.length"
      class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_EMPTY') }}
    </div>

    <div
      v-else-if="!filteredBlockedUsers.length"
      class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_FILTER_EMPTY') }}
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="user in filteredBlockedUsers"
        :key="user.wa_id || user.input"
        class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ user.wa_id || user.user || user.user_id || user.input }}
            </p>
            <p
              v-if="user.input && user.input !== (user.wa_id || user.user || user.user_id)"
              class="mt-1 mb-0 text-xs text-n-slate-11"
            >
              {{ user.input }}
            </p>
          </div>
          <Button
            type="button"
            variant="faded"
            color="slate"
            icon="i-ph-lock-open"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_UNBLOCK')"
            :is-loading="updatingWaId === (user.wa_id || user.user || user.user_id)"
            @click="unblockUser(user.wa_id || user.user || user.user_id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
